import { TableColumnHeader } from "@/components/shared/table/Table";
import { Tender, UserOnEntity } from "@/graphql/__generated__/graphql";
import { formatDateLocale, convertBpToDecimal, formatNumberLocale, getUserFriendlyTenderState, handleTenderStateColour } from "@/utils/helpers";
import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { IntlShape } from "react-intl";
import { DecisionGroup, DecisionGroupCircles } from "@/components/shared/DecisionGroupCircles";
import { UserAvatar } from "@/components/shared/common";
import { NavigateFunction } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";

/**
 * Handles navigation when a specific decision group circle is clicked
 *
 * @param {DecisionGroup} group - The decision group that was clicked
 * @param {Tender} row - The tender that was clicked
 * @param {NavigateFunction} navigate - The navigate function from react-router-dom
 */
const onGroupClickHandler = (group: DecisionGroup, row: Tender, navigate: NavigateFunction) => {
  // If no group is selected, return
  if (!group) return;

  // Find the matching group in the appropriate decision groups array
  const groups = row.bidDecisionGroups;

  // Find the matching group by operationalOrganizationId
  const matchingGroup = groups.find(
    (g: any) => g.operationalOrganizationId === group.operationalOrganizationId
  );

  if (!matchingGroup) return;

  // Create the navigation path based on the decision type and group ID
  const path = `/tender/${row.id}/biddecisions/${matchingGroup.id}?openAccordion=item-0`;

  navigate(path);
};

export const getColumns = (intl: IntlShape, t: TFunction, actionFn?: any, navigate?: NavigateFunction): ColumnDef<Tender, string>[] => {
  return [
    {
      id: "nameAndState",
      header: ({ column }) => <TableColumnHeader text={t("tables:tenders.columns.name")} column={column} />,
      cell: ({ row }) => (
        <div className="flex flex-col space-y-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="font-bold max-w-[300px] truncate text-xs">
                {row.original.description.name}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="w-auto">{row.original.description.name}</p>
            </TooltipContent>
          </Tooltip>
          <div className="flex flex-row items-center gap-1">
            {
              row.original.hasRecentMessages && (
                <div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ExclamationCircleIcon className="size-4 text-red-500"/>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="w-auto">
                        {t("tables:tenders.recentmessages")}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )
            }
            <Badge className={cn("border-0 state-badge text-[10px]", handleTenderStateColour(row.original.state, true, true, false))}>
              {getUserFriendlyTenderState(t, row.original.state)}
            </Badge>
          </div>
        </div>
      ),
      meta: { action: actionFn },
    },
    {
      accessorKey: "description.customerName",
      header: ({ column }) => <TableColumnHeader text={t("tables:tenders.columns.customer")} column={column} />,
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="max-w-[200px] truncate">
              {row.original.customer.name}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="w-auto">{row.original.customer.name}</p>
          </TooltipContent>
        </Tooltip>
      ),
      meta: { action: actionFn },
    },
    {
      id: "operationOrgs",
      accessorFn: (row) => {
        // Sort by cumulated operationalOrganizationId values
        if (!row.bidDecisionGroups || row.bidDecisionGroups.length === 0) return "0";

        // Sum up all operationalOrganizationId values
        return row.bidDecisionGroups.reduce((sum, group) => {
          return sum + (group.operationalOrganizationId || 0);
        }, 0).toString();
      },
      header: ({ column }) => <TableColumnHeader text={t("tables:tenders.columns.operationalorgs")} column={column} align="start" />,
      cell: ({ row }) =>
        <DecisionGroupCircles
          groups={row.original.bidDecisionGroups}
          size="default"
          onGroupClick={(group) => navigate ? onGroupClickHandler(group, row.original, navigate) : undefined}
        />,
      meta: { action: actionFn },
    },
    {
      id: "description.owner",
      accessorFn: (row) => {
        // Sort by owner's last name then first name
        return `${row.description.owner?.lastname || ""} ${row.description.owner?.firstname || ""}`;
      },
      header: ({ column }) => <TableColumnHeader text={t("tables:tenders.columns.owner")} column={column} align="start" />,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <UserAvatar user={row.original.description.owner as UserOnEntity} />
          {row.original.description.owner?.firstname} {row.original.description.owner?.lastname}
        </div>
      ),
      meta: { action: actionFn },
    },
    {
      accessorKey: "margin",
      header: ({ column }) => <TableColumnHeader text={t("tables:tenders.columns.margin")} column={column} align="start" />,
      // divided by 100 to convert from percentage to decimal so formateNumberLocale works correctly
      cell: ({ row }) => <div>{formatNumberLocale(intl, convertBpToDecimal((row.original.margin ?? row.original.estimate.margin) / 100), "percent")}</div>,
      meta: { action: actionFn },
    },
    {
      accessorKey: "revenue",
      header: ({ column }) => <TableColumnHeader text={t("tables:tenders.columns.revenue")} column={column} align="start" />,
      cell: ({ row }) => <div>{formatNumberLocale(intl, row.original.revenue, "currency")}</div>,
      meta: { action: actionFn },
    },
    {
      accessorKey: "surcharge",
      header: ({ column }) => <TableColumnHeader text={t("tables:tenders.columns.surcharge")} column={column} align="start" />,
      cell: ({ row }) => <div>{`${convertBpToDecimal(row.original.surcharge)}%`}</div>,
      meta: { action: actionFn },
    },
    {
      accessorKey: "deadline.submission",
      header: ({ column }) => <TableColumnHeader text={t("tables:tenders.columns.deadline")} column={column} />,
      cell: ({ row }) => formatDateLocale(intl, row.original.deadline.submission),
      meta: { action: actionFn },
    },
    {
      accessorKey: "contract.startDate",
      header: ({ column }) => <TableColumnHeader text={t("tables:tenders.columns.startdate")} column={column} />,
      cell: ({ row }) => formatDateLocale(intl, row.original.contract.startDate),
      meta: { action: actionFn },
    },

  ];
};
